/**
 * The ONE owner of `GET /api/v1/me/surfaces` (SPEC-TENANT-SURFACES API-001).
 *
 * SPEC-FRONTEND-RULE-077: one cache key, one fetcher, one owner. Every reader
 * of the resolved surface set goes through {@link surfacesQueryOptions} - the
 * hooks in `./useSurface`, the prefetch in `apps/tenant/App.tsx` and the
 * refusal handler in `./surfaceRefusal` - so the QUERY-KEY-GATE sees exactly
 * one declaration of `["me", "surfaces"]`.
 *
 * WHAT THIS MODULE DOES NOT HOLD (RULE-016): no surface tree and no key list.
 * The registry is a `config/contracts/` artifact `web/src` cannot import; on the
 * frontend "the registry" is the RESOLVED rows this endpoint returns. The types
 * below mirror API-001's shape exactly and add nothing.
 *
 * STALENESS (RULE-015, client side of the same bound): the answer is stale
 * after {@link SURFACES_STALE_TIME_MS} (the gateway's 30 s cache backstop) and
 * is refetched on window focus and on reconnect, so an admin's hide reaches
 * the SPA within the bound the refusal gate works to - never "for the life of
 * the tab", which is what the SPA-wide 5 minute default would give.
 *
 * @module surfaces/surfacesQuery
 * @see SPEC-TENANT-SURFACES-API-001
 * @see SPEC-TENANT-SURFACES-RULE-014 / RULE-015 / RULE-016
 * @see TASK-TSURF-W3-SURFACES-STORE-FE-CHG-001
 */
import { queryOptions } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import client from "../api/client";
import type { SurfaceKey } from "./keys";

// ---------------------------------------------------------------------------
// API-001 shape (mirrored, nothing added)
// ---------------------------------------------------------------------------

/**
 * The state of a surface the caller MAY use. A hidden surface is ABSENT from
 * the map, never present as `hidden` (API-001), so only these two appear.
 */
export type SurfaceState = "visible" | "advanced";

/** The tenant's own resolution of a surface; carried only when `unrestricted` (RULE-012). */
export type SurfaceTenantState = "visible" | "advanced" | "hidden";

/** Registry row kind (SPEC-TENANT-SURFACES RULE-017). */
export type SurfaceKind = "nav_group" | "nav" | "page" | "section";

/** One resolved row of API-001: the state beside the MANDATORY registry fields. */
export interface ResolvedSurfaceRow {
  state: SurfaceState;
  /** Present ONLY when the answer's `unrestricted` is true (RULE-012). */
  tenant_state?: SurfaceTenantState;
  label_msgid: string;
  parent: string;
  kind: SurfaceKind;
}

/** The body of `GET /api/v1/me/surfaces` (API-001). */
export interface MeSurfacesResponse {
  surfaces: Record<SurfaceKey, ResolvedSurfaceRow>;
  advanced_enabled: boolean;
  unrestricted: boolean;
  /**
   * The tenant's agent start page (D-TSG-32): a key this caller can use, or
   * null. Optional because an older gateway does not send it.
   */
  agent_start_surface?: SurfaceKey | null;
  /** Per-agent exceptions, agent id -> key; listed only when usable (D-TSG-32). */
  agent_start_overrides?: Record<string, SurfaceKey>;
}

// ---------------------------------------------------------------------------
// Query key, fetcher, options
// ---------------------------------------------------------------------------

/** RULE-015 client bound: never staler than the gateway's 30 s cache backstop. */
export const SURFACES_STALE_TIME_MS = 30_000;

/** The ONE cache key of the resolved surface set. */
export function surfacesQueryKey() {
  return ["me", "surfaces"] as const;
}

/**
 * A 200 whose body is not the API-001 shape. Thrown rather than coerced to an
 * empty map: an empty map would read as "everything hidden by choice", which
 * API-001's 503 row forbids the SPA to infer from a failure.
 */
export class MalformedSurfacesResponseError extends Error {
  constructor(detail: string) {
    super(`GET /me/surfaces returned a body that is not the API-001 shape: ${detail}`);
    this.name = "MalformedSurfacesResponseError";
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export async function fetchSurfaces(): Promise<MeSurfacesResponse> {
  const { data } = await client.get<unknown>("/me/surfaces");
  if (!isPlainObject(data)) {
    throw new MalformedSurfacesResponseError("body is not an object");
  }
  if (!isPlainObject(data.surfaces)) {
    throw new MalformedSurfacesResponseError("`surfaces` is not an object");
  }
  if (typeof data.advanced_enabled !== "boolean" || typeof data.unrestricted !== "boolean") {
    throw new MalformedSurfacesResponseError("`advanced_enabled` / `unrestricted` are not booleans");
  }
  return data as unknown as MeSurfacesResponse;
}

/**
 * Retry policy of the bootstrap read. An auth refusal (401 / 403) is not
 * transient: repeating it only delays the typed error the layouts render
 * (RULE-014), so it is not retried. A 503 or a network failure is retried once,
 * the SPA-wide default; the layouts' Retry covers the rest.
 */
function shouldRetrySurfaces(failureCount: number, error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === 401 || status === 403) return false;
  if (error instanceof MalformedSurfacesResponseError) return false;
  return failureCount < 1;
}

export function surfacesQueryOptions() {
  return queryOptions({
    queryKey: surfacesQueryKey(),
    queryFn: fetchSurfaces,
    staleTime: SURFACES_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: shouldRetrySurfaces,
  });
}

// ---------------------------------------------------------------------------
// Typed failure (RULE-014; SPEC-FRONTEND-RULE-042: auth failure is not empty)
// ---------------------------------------------------------------------------

/**
 * Why the resolved set is unavailable. `unauthenticated` (401), `forbidden`
 * (403) and `unavailable` (503) are the three statuses API-001 names; the
 * layouts name each one and never report "no surfaces".
 */
export type SurfacesErrorReason =
  | "unauthenticated"
  | "forbidden"
  | "unavailable"
  | "network"
  | "malformed"
  | "unexpected";

export interface SurfacesError {
  reason: SurfacesErrorReason;
  /** The HTTP status, when there was a response. */
  status?: number;
}

export function classifySurfacesError(error: unknown): SurfacesError {
  if (error instanceof MalformedSurfacesResponseError) {
    return { reason: "malformed" };
  }
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status === undefined) return { reason: "network" };
    if (status === 401) return { reason: "unauthenticated", status };
    if (status === 403) return { reason: "forbidden", status };
    if (status === 503) return { reason: "unavailable", status };
    return { reason: "unexpected", status };
  }
  return { reason: "unexpected" };
}
